import { AdvisorServicesAPI, CardAPI, debug, NetbankingAPI, observable } from "aaa";
import { ui } from "components/util/uiHelpers";
import { flatMap } from "lodash";
import { AdvisorServicesAPIClient, CardAPIClient, NetbankingAPIClient } from "network/APIClient";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { LimitsStore } from "stores/LimitsStore";
import { isNewCreditCard } from "stores/OffersStore";
import * as amount from "types/amount";
import { ICreditCardPurchaseResponse } from "types/card";
import { IContact } from "types/contact";
import { ILandingPageCondition } from "types/landingPage";
import { ICreditCardOfferDetails } from "types/offer";
import { ShopStore } from "../shop/ShopStore";

const PREFERRED_APPOINTMENT_TOPICS = ["KONKA", "ABER"] as const;
export class CreditCardBusinessStore extends ShopStore {
    @observable purchaseInfo?: ICreditCardPurchaseResponse;
    @observable limitsStore: LimitsStore;
    @observable conditions?: ILandingPageCondition[];
    @observable milesAndMoreCardNumber?: string;


    constructor() {
        super({
            loadContacts: true,
            loadAccounts: true,
            loadProfile: true,

            accountsUsableForQuery: "BUSINESS_CREDIT_CARD",
            accountsUsableForFilter: "businessCreditCard",
        });

        this.limitsStore = new LimitsStore();
    }

    protected onOfferReceived() {
        super.onOfferReceived();
    }

    async load(productId: string, conditionsGroupId: string, channelProduct: string) {
        debug.log("### CreditCardBusinessStore load");

        try {
            // Prepare for a potential appointment booking
            const topicsTarget = AdvisorServicesAPI.getTopics();
            const topicsData = await AdvisorServicesAPIClient.requestType(topicsTarget);
            const topics = topicsData.topics;

            // get the first matching topic of preferredTopics and set it for appointment feature
            const preferredTopic = flatMap(PREFERRED_APPOINTMENT_TOPICS, validTopic => topics.find(topic => topic.id === validTopic))?.[0];
            externalInterfaceStore.topicId = preferredTopic?.id;

            await super.load(productId, conditionsGroupId, channelProduct, true);

            const conditionShortCodeTexts = {
                "KA-KKERST": ui.__("shop.creditCardBusiness.condition.feeFirstYear"),
                "KA-KK": ui.__("shop.creditCardBusiness.condition.feeFromSecondYear")
            };

            this.conditions = Object.keys(conditionShortCodeTexts).map(conditionShortCode => {
                const condition = (this.offerDetails as ICreditCardOfferDetails).productDetails.find(detail => detail.conditionShortCode === conditionShortCode);

                return ({
                    label: condition?.fullConditionName ?? conditionShortCodeTexts[conditionShortCode],
                    value: amount.amountToCurrencyString(condition?.amount || amount.ZERO_AMOUNT)
                });
            });

            await this.limitsStore.load();
        } catch (error) {
            throw error;
        }
    }

    async loadAddressesByAccountId(accountId?: string) {
        debug.log("### load addresses by account ID", accountId);
        this.contactsStore.selectedAddressId = undefined;

        if (!accountId) {
            this.contactsStore.addresses = [];
            return;
        }

        try {
            this.isLoadingAddresses = true;
            const target = CardAPI.getContactsForBusinessCardsInRetail(accountId);
            const addresses = await CardAPIClient.requestType(target);

            const contactAddress = addresses.map(address => {
                const flags = address.mainContact ? ["mainContact"] : [];
                const contact: IContact = {
                    id: address.id,
                    type: "ADDRESS",
                    flags,
                    address: {
                        street: address.street,
                        zipCode: address.zipCode,
                        city: address.city,
                        country: address.country,
                        type: "",
                        typeI18N: "",
                    },
                }
                return contact;
            });

            this.contactsStore.addresses = contactAddress ?? [];
            debug.log("### load addresses by account ID", contactAddress);
            this.contactsStore.selectedAddressId = this.contactsStore.addresses?.[0].id;

        } catch (error) {
            this.contactsStore.addresses = [];
            throw error;
        } finally {
            this.isLoadingAddresses = false;
        }

    }

    async purchaseCreditCard() {
        const channelProduct = externalInterfaceStore.channelProduct;

        debug.log("### purchaseCreditCard");
        if (!this.contactsStore.selectedEmailAddressId ||
            !this.offer ||
            !this.accountsStore.selectedAccount) {
            debug.log("### missing field");
            return;
        }

        const isNewCreditCardToPurchase = channelProduct && isNewCreditCard(channelProduct);

        const target = NetbankingAPI.purchaseCreditCard({
            productId: this.offer.productId,
            conditionsGroupId: this.offer.conditionsGroupId,
            creditCardLimit: this.cardCharacteristic === "prepaid" ? "Z" : this.limitsStore.selectedLimitShortCode,
            payerAccountId: this.accountsStore.selectedAccount.id,
            paybackMethod: this.repaymentChargeCard ? "CHARGE_CARD" : "REVOLVING_CREDIT",
            paybackPercent: this.revolvingPercentage,
            paybackAmount: amount.numberToEuroAmount(this.revolvingMinRate),
            deliveryAddress: this.contactsStore.selectedAddressId,
            infoAboSms: this.contactsStore.watchdogActive && !isNewCreditCardToPurchase ? this.contactsStore.selectedPhoneId : undefined,
            infoAboEmail: isNewCreditCardToPurchase ? undefined : this.contactsStore.selectedEmailAddressId,
            smsService: this.contactsStore.watchdogActive,
            email: this.contractEmailId || this.contactsStore.emailAddresses[0].id,
            cardFormFactor: this.cardFormFactor?.toUpperCase(),
            milesAndMoreCardNumber: this.milesAndMoreCardNumber,
        });

        try {
            this.isLoadingPurchase = true;
            this.purchaseInfo = await NetbankingAPIClient.requestType(target);
            debug.log("### purchaseCreditCard response", this.purchaseInfo);
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingPurchase = false;
        }
    }

    getProductDetailsForConditionShortCode(conditionShortCode: string) {
        if (!this.offerDetails) {
            return;
        }

        const details = this.offerDetails as ICreditCardOfferDetails;
        const productDetails = details.productDetails.find(entry => entry.conditionShortCode === conditionShortCode);
        return productDetails;
    }

    async getIsCardNameValid(productId: string, accountId: string, conditionGroupId: string) {
        try {
            this.isLoadingAddresses = true;
            const target = CardAPI.getIsCardNameValid(accountId, productId, conditionGroupId);
            const response = await CardAPIClient.requestType(target);
            return response;
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingAddresses = false;
        }
    }
}


// WEBPACK FOOTER //
// ./src/components/shopCreditCardBusiness/CreditCardBusinessStore.ts